/**
 * ui/LicenseManager — gestion de la licence du serveur depuis l'admin.
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * Le serveur est le POINT D'ACTIVATION du site ; l'admin (admin.exe / console)
 * la PILOTE via `/api/license/*` (réservé admin). Deux façades :
 *   • EN LIGNE  — saisir le code → le SERVEUR télécharge la licence (activate) ;
 *   • HORS-LIGNE — l'admin importe un fichier licence signé qu'il a téléchargé
 *     lui-même → upload au serveur qui le vérifie LOCALEMENT (import).
 * Le code d'activation n'est jamais renvoyé par le serveur (on n'affiche que sa
 * présence). En mode console, `api` cible déjà le site distant relayé.
 */
import React, { useEffect, useRef, useState } from 'react';

const OK = new Set(['ACTIVE']);
const WARN = new Set(['EXPIRED', 'UNREACHABLE', 'NO_CODE', 'UNINITIALIZED']);

/** Seuil d'alerte : un mois avant l'échéance, le renouvellement doit être engagé. */
const WARN_DAYS = 30;

/** Jours restants avant expiration ; `null` si la licence est perpétuelle. */
function daysLeft(info) {
  if (!info?.expiresAt) return null;
  const ms = new Date(info.expiresAt).getTime() - Date.now();
  return Number.isNaN(ms) ? null : Math.ceil(ms / 86400000);
}

function statusBadge(info) {
  if (!info) return { label: '…', cls: 'badge--muted' };
  if (info.valid && OK.has(info.status)) return { label: `✓ ${info.status}`, cls: 'badge--ok' };
  if (WARN.has(info.status)) return { label: info.status, cls: 'badge--user' };
  return { label: info.status || 'INVALIDE', cls: 'badge--err' };
}

export default function LicenseManager({ api }) {
  const [info, setInfo] = useState(null);
  const [code, setCode] = useState('');
  const [project, setProject] = useState('');
  const [enforce, setEnforce] = useState(false);
  const [reqEmail, setReqEmail] = useState('');
  const [reqCompany, setReqCompany] = useState('');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState('');
  // Affichage de la licence sur la fiche des services du distributeur (chez le client).
  const [showOnDistributor, setShowOnDistributor] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const data = await api.getLicense();
      setInfo(data);
      setEnforce(!!data?.enforce);
      setProject(data?.project || '');
    } catch (e) {
      setStatus(e?.response?.status === 403 ? 'Réservé au rôle admin.' : String(e?.message || e));
    }
    // Ce réglage vit dans la config (relayée au distributeur), pas dans l'état de licence.
    try {
      const cfg = await api.getConfig();
      setShowOnDistributor(cfg?.license?.showOnDistributor === true);
    } catch {
      // Config illisible (droits/réseau) : on laisse la case à « non affiché ».
    }
  };

  /**
   * Active/désactive le rappel de licence sur le distributeur.
   *
   * `PUT /api/config` fusionne au PREMIER niveau seulement : on relit la clé `license`
   * et on la réécrit entière, sinon les réglages voisins seraient effacés.
   */
  const toggleShowOnDistributor = async (val) => {
    setShowOnDistributor(val);
    setBusy('showdist'); setStatus('');
    try {
      const cfg = await api.getConfig();
      await api.saveConfig({
        license: { ...(cfg?.license || {}), showOnDistributor: val, warnDays: WARN_DAYS },
      });
      setStatus(val
        ? '✓ Le rappel de licence est maintenant affiché sur le distributeur du client.'
        : '✓ Le distributeur n\'affiche plus la licence (elle reste visible si elle devient invalide).');
    } catch (e) {
      setShowOnDistributor(!val); // rétablir la case : la config n'a pas été enregistrée
      setStatus(`Échec : ${e?.message || e}`);
    } finally {
      setBusy('');
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const activate = async () => {
    setBusy('activate'); setStatus('');
    try {
      const data = await api.activateLicense({
        activationCode: code.trim() || undefined,
        project: project.trim() || undefined,
        enforce,
      });
      setInfo(data); setCode('');
      setStatus(data?.valid ? '✓ Licence activée.' : `Statut : ${data?.status}${data?.reason ? ' — ' + data.reason : ''}`);
    } catch (e) {
      setStatus(e?.response?.status === 403 ? 'Réservé au rôle admin.' : `Échec : ${e?.message || e}`);
    } finally { setBusy(''); }
  };

  const saveProject = async () => {
    setBusy('project'); setStatus('');
    try {
      const data = await api.activateLicense({ project: project.trim() });
      setInfo(data);
      setStatus(data?.valid
        ? `✓ Projet « ${data.project} » — licence valide.`
        : `Projet enregistré. Statut : ${data?.status}${data?.reason ? ' — ' + data.reason : ''}`);
    } catch (e) {
      setStatus(`Échec : ${e?.message || e}`);
    } finally { setBusy(''); }
  };

  // L'envoi de l'email est une affaire d'ADMIN (jamais le serveur de site) :
  //  • admin.exe (Electron) → process main via IPC `window.adminLicense.requestCode` ;
  //  • console centrale (navigateur) → control-plane, endpoint same-origin.
  const requestCode = async () => {
    setBusy('request'); setStatus('');
    const payload = {
      email: reqEmail.trim(), project: project.trim(),
      company: reqCompany.trim(), machineId: info?.machineId,
    };
    try {
      let result;
      if (window.adminLicense?.requestCode) {
        result = await window.adminLicense.requestCode(payload);
        if (result?.error) throw new Error(result.error);
      } else {
        const r = await fetch('/api/console/license/request-code', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok || !j.success) throw new Error(j.error || `HTTP ${r.status}`);
        result = j.data;
      }
      if (result?.driver === 'console') {
        setStatus('⚠️ SMTP non configuré : email seulement journalisé (driver console), pas réellement envoyé.');
      } else {
        setStatus(`✓ Demande envoyée à ${result?.to || 'l\'admin des licences'}. Le code sera transmis à ${reqEmail.trim()}.`);
      }
    } catch (e) {
      setStatus(`Échec de la demande : ${e?.message || e}`);
    } finally { setBusy(''); }
  };

  const refresh = async () => {
    setBusy('refresh'); setStatus('');
    try { setInfo(await api.refreshLicense()); setStatus('✓ Re-vérifié.'); }
    catch (e) { setStatus(`Échec : ${e?.message || e}`); }
    finally { setBusy(''); }
  };

  const importFile = async (file) => {
    if (!file) return;
    setBusy('import'); setStatus('');
    try {
      const signed = JSON.parse(await file.text());
      const data = await api.importLicense(signed);
      setInfo(data);
      setStatus(data?.valid
        ? '✓ Licence importée et vérifiée localement.'
        : `Importée mais ${data?.status}${data?.reason ? ' — ' + data.reason : ''}`);
    } catch (e) {
      setStatus(`Import invalide : ${e?.message || e}`);
    } finally {
      setBusy('');
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const setEnforceOnly = async (val) => {
    setEnforce(val);
    setBusy('enforce'); setStatus('');
    try { setInfo(await api.activateLicense({ enforce: val })); }
    catch (e) { setStatus(`Échec : ${e?.message || e}`); }
    finally { setBusy(''); }
  };

  const badge = statusBadge(info);
  const remaining = daysLeft(info);
  // Alerte à un mois : c'est le signal qui déclenche la démarche de renouvellement.
  const expiringSoon = remaining !== null && remaining <= WARN_DAYS;

  return (
    <div className="license-mgr">
      <h2 style={{ marginTop: 0 }}>Gestion de la licence</h2>

      {expiringSoon && (
        <div className="license-alert">
          {remaining > 0
            ? `⚠ Licence à renouveler — expire dans ${remaining} jour${remaining > 1 ? 's' : ''}.`
            : '⚠ Licence expirée.'}
          {' '}Activez le rappel ci-dessous pour l&apos;afficher sur le distributeur du client.
        </div>
      )}

      <section className="license-status">
        <div className="license-row">
          <span className="muted">Statut</span>
          <span className={`badge ${badge.cls}`}>{badge.label}</span>
          {info?.offline && <span className="badge badge--muted">hors-ligne (cache)</span>}
          {info?.enforce && <span className="badge badge--err">blocage actif</span>}
        </div>
        <div className="license-row"><span className="muted">Projet</span><span>{info?.project || '—'}</span></div>
        <div className="license-row">
          <span className="muted">Expiration</span>
          <span className={expiringSoon ? 'license-expiry--soon' : undefined}>
            {info?.expiresAt ? new Date(info.expiresAt).toLocaleString() : '—'}
            {remaining !== null && (
              <> {remaining > 0 ? `(${remaining} jour${remaining > 1 ? 's' : ''} restant${remaining > 1 ? 's' : ''})` : '(expirée)'}</>
            )}
          </span>
        </div>
        <div className="license-row"><span className="muted">Code posé</span><span>{info?.hasCode ? 'oui' : 'non'}</span></div>
        <div className="license-row"><span className="muted">Vérifié</span><span>{info?.checkedAt ? new Date(info.checkedAt).toLocaleString() : '—'}</span></div>
        <div className="license-row">
          <span className="muted">ID machine</span>
          <code className="license-mid">{info?.machineId || '—'}</code>
          {info?.machineId && (
            <button className="ghost" onClick={() => navigator.clipboard?.writeText(info.machineId)}>copier</button>
          )}
        </div>
        <button className="ghost" onClick={refresh} disabled={busy === 'refresh'}>
          {busy === 'refresh' ? '…' : '↻ Re-vérifier maintenant'}
        </button>
      </section>

      {status && <div className="status">{status}</div>}

      <section className="license-facade">
        <h4>Projet attendu</h4>
        <p className="muted">Nom du projet de la licence (doit correspondre EXACTEMENT à celui émis par le serveur de licences). Un écart provoque le statut <code>WRONG_PROJECT</code>.</p>
        <div className="license-actions">
          <input
            type="text" placeholder="ex. Cabinet Medical El-Shifa" value={project}
            onChange={(e) => setProject(e.target.value)} autoComplete="off"
          />
          <button onClick={saveProject} disabled={busy === 'project' || !project.trim()}>
            {busy === 'project' ? '…' : 'Enregistrer le projet'}
          </button>
        </div>
      </section>

      <section className="license-facade">
        <h4>En ligne — le serveur télécharge</h4>
        <p className="muted">Saisir le code d'activation ; le serveur récupère la licence (du projet ci-dessus) depuis le serveur de licences. Nécessite qu'il ait accès à Internet.</p>
        <div className="license-actions">
          <input
            type="text" placeholder="Code d'activation" value={code}
            onChange={(e) => setCode(e.target.value)} autoComplete="off"
          />
          <button onClick={activate} disabled={busy === 'activate' || !code.trim()}>
            {busy === 'activate' ? '…' : 'Activer'}
          </button>
        </div>
      </section>

      <section className="license-facade">
        <h4>Réclamer un code d'activation</h4>
        <p className="muted">Envoie une demande au serveur de licences (email + projet + société). Le code vous est transmis par email — il n'y a pas d'authentification par mot de passe côté serveur de licences.</p>
        <div className="license-actions">
          <input
            type="email" placeholder="Email" value={reqEmail}
            onChange={(e) => setReqEmail(e.target.value)} autoComplete="off"
          />
          <input
            type="text" placeholder="Société (optionnel)" value={reqCompany}
            onChange={(e) => setReqCompany(e.target.value)} autoComplete="off"
          />
          <button onClick={requestCode} disabled={busy === 'request' || !reqEmail.trim() || !project.trim()}>
            {busy === 'request' ? '…' : 'Demander le code'}
          </button>
        </div>
      </section>

      <section className="license-facade">
        <h4>Hors-ligne — importer un fichier licence</h4>
        <p className="muted">Pour un serveur sans Internet : téléchargez la licence signée (portail de licences) puis importez le fichier ici. Le serveur la vérifie localement (signature).</p>
        <div className="license-actions">
          <button onClick={() => fileRef.current?.click()} disabled={busy === 'import'}>
            {busy === 'import' ? '…' : '⤒ Importer un fichier licence (.json/.lic)'}
          </button>
          <input
            ref={fileRef} type="file" accept="application/json,.json,.lic"
            style={{ display: 'none' }}
            onChange={(e) => importFile(e.target.files?.[0])}
          />
        </div>
      </section>

      <section className="license-facade">
        <h4>Politique</h4>
        <label className="license-enforce">
          <input
            type="checkbox" checked={enforce} disabled={busy === 'enforce'}
            onChange={(e) => setEnforceOnly(e.target.checked)}
            style={{ width: 'auto' }}
          />
          <span>Blocage si licence invalide (le distributeur et le guichet refusent le service)</span>
        </label>

        <label className="license-enforce">
          <input
            type="checkbox" checked={showOnDistributor} disabled={busy === 'showdist'}
            onChange={(e) => toggleShowOnDistributor(e.target.checked)}
            style={{ width: 'auto' }}
          />
          <span>
            Afficher la licence sur le distributeur du client (fiche des services)
            {expiringSoon && <strong className="license-expiry--soon"> — recommandé, échéance proche</strong>}
          </span>
        </label>
        <p className="muted">
          Décoché, la fiche des services ne mentionne rien tant que la licence est valide.
          Une licence <em>invalide</em> reste signalée dans tous les cas. À moins d&apos;un mois de
          l&apos;échéance, le rappel s&apos;affiche en rouge chez le client pour l&apos;inciter à renouveler.
        </p>
      </section>
    </div>
  );
}
